import { useEffect, useState } from "react";
import { Mail, Inbox, Trash2, Loader2, Phone, Building2, Check, Download } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { fetchContacts, deleteContact, markContactRead, fetchSubscribers, deleteSubscriber } from "../lib/api";

export default function AdminDashboard() {
  const [contacts, setContacts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([fetchContacts(), fetchSubscribers()]);
      setContacts(c); setSubs(s);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onDeleteContact = async (id) => {
    await deleteContact(id); setContacts((p) => p.filter((x) => x.id !== id)); toast.success("Inquiry deleted");
  };
  const onRead = async (id) => {
    await markContactRead(id); setContacts((p) => p.map((x) => x.id === id ? { ...x, read: true } : x));
  };
  const onDeleteSub = async (id) => {
    await deleteSubscriber(id); setSubs((p) => p.filter((x) => x.id !== id)); toast.success("Subscriber removed");
  };

  const exportSubs = () => {
    const csv = "email,subscribed_at\n" + subs.map((s) => `${s.email},${s.created_at}`).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "subscribers.csv"; a.click(); URL.revokeObjectURL(url);
  };

  const unread = contacts.filter((c) => !c.read).length;

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-heading text-2xl font-semibold">Dashboard</h1>
      <p className="mt-1 text-sm text-muted-foreground">Manage incoming inquiries and newsletter subscribers.</p>

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Stat icon={Inbox} label="Total Inquiries" value={contacts.length} />
        <Stat icon={Mail} label="Unread" value={unread} accent />
        <Stat icon={Mail} label="Subscribers" value={subs.length} />
      </div>

      <Tabs defaultValue="inquiries" className="mt-8">
        <TabsList>
          <TabsTrigger value="inquiries" data-testid="tab-inquiries">Inquiries ({contacts.length})</TabsTrigger>
          <TabsTrigger value="subscribers" data-testid="tab-subscribers">Subscribers ({subs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="inquiries" className="mt-5">
          {contacts.length === 0 ? <Empty text="No inquiries yet." /> : (
            <div className="space-y-3">
              {contacts.map((c) => (
                <div key={c.id} className={`rounded-xl border bg-card p-5 ${c.read ? "border-border" : "border-primary/40 ring-1 ring-primary/20"}`} data-testid="inquiry-row">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading font-semibold">{c.name}</h3>
                        {!c.read && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase text-primary">New</span>}
                        {c.service && <span className="rounded-full bg-secondary/10 px-2 py-0.5 text-[10px] font-semibold text-secondary">{c.service}</span>}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {c.email}</span>
                        {c.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {c.phone}</span>}
                        {c.company && <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" /> {c.company}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!c.read && <button onClick={() => onRead(c.id)} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted" data-testid="mark-read-btn"><Check className="h-3.5 w-3.5" /> Read</button>}
                      <button onClick={() => onDeleteContact(c.id)} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-destructive hover:bg-destructive/10" data-testid="delete-inquiry-btn"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                  <p className="mt-3 whitespace-pre-wrap text-sm text-foreground/90">{c.message}</p>
                  <p className="mt-2 text-[11px] text-muted-foreground">{new Date(c.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="subscribers" className="mt-5">
          <div className="mb-4 flex justify-end">
            <button onClick={exportSubs} disabled={!subs.length} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50" data-testid="export-subs-btn"><Download className="h-4 w-4" /> Export CSV</button>
          </div>
          {subs.length === 0 ? <Empty text="No subscribers yet." /> : (
            <div className="divide-y divide-border rounded-xl border border-border bg-card">
              {subs.map((s) => (
                <div key={s.id} className="flex items-center justify-between px-5 py-3" data-testid="subscriber-row">
                  <span className="text-sm">{s.email}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleDateString()}</span>
                    <button onClick={() => onDeleteSub(s.id)} className="text-destructive hover:opacity-70" data-testid="delete-sub-btn"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${accent ? "bg-accent/15 text-accent" : "bg-primary/10 text-primary"}`}><Icon className="h-5 w-5" /></span>
      <p className="mt-3 font-heading text-2xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function Empty({ text }) {
  return <div className="rounded-xl border border-dashed border-border py-16 text-center text-sm text-muted-foreground">{text}</div>;
}
